import { useApi } from '@/api/useApi.ts';
import {
    IBenchmarkPoolDefinition,
    IBenchmarkPoolDefinitionInput,
    IBenchmarkPoolPreviewRequest,
    IBenchmarkPoolRecord,
    ILeaderboardBenchmarkPoolConfig,
    IResolvedBenchmarkPool,
} from '@/interfaces/powerMarkets/leaderboard/IBenchmarkPool.ts';
import {
    benchmarkPoolDefinitionToRecord,
    benchmarkPoolRecordToInput,
} from '@/helpers/powerMarkets/benchmarkPoolDefinitionHelpers.ts';

const seriesApi = useApi('series');
const definitionsEndpoint = '/GbBenchmarkPoolDefinitions';

async function listBenchmarkPools (): Promise<Array<IBenchmarkPoolRecord>> {
    const response = await seriesApi.get<Array<IBenchmarkPoolDefinition>>({ endpoint: definitionsEndpoint });
    return response.data.map(benchmarkPoolDefinitionToRecord);
}

async function getBenchmarkPool (id: string): Promise<IBenchmarkPoolRecord> {
    const response = await seriesApi.get<IBenchmarkPoolDefinition>({
        endpoint: `${definitionsEndpoint}/${encodeURIComponent(id)}`,
    });
    return benchmarkPoolDefinitionToRecord(response.data);
}

export interface ICreateBenchmarkPoolPayload {
    name: string;
    config: ILeaderboardBenchmarkPoolConfig;
}

async function createBenchmarkPool (payload: ICreateBenchmarkPoolPayload): Promise<IBenchmarkPoolRecord> {
    const response = await seriesApi.post<IBenchmarkPoolDefinitionInput, IBenchmarkPoolDefinition>({
        endpoint: definitionsEndpoint,
        payload: benchmarkPoolRecordToInput(payload),
    });
    return benchmarkPoolDefinitionToRecord(response.data);
}

async function updateBenchmarkPool (record: IBenchmarkPoolRecord): Promise<IBenchmarkPoolRecord> {
    const response = await seriesApi.put<IBenchmarkPoolDefinitionInput, IBenchmarkPoolDefinition>({
        endpoint: `${definitionsEndpoint}/${encodeURIComponent(record.id)}`,
        payload: benchmarkPoolRecordToInput(record),
    });
    return benchmarkPoolDefinitionToRecord(response.data);
}

async function deleteBenchmarkPool (id: string): Promise<void> {
    await seriesApi.delete<void>({ endpoint: `${definitionsEndpoint}/${encodeURIComponent(id)}` });
}

type IBenchmarkPoolPreviewResponse = Omit<IResolvedBenchmarkPool, 'historicalExclusions' | 'excludedPlantIdsWithoutCoordinates'> & {
    historicalExclusions?: IResolvedBenchmarkPool['historicalExclusions'];
    excludedPlantIdsWithoutCoordinates?: IResolvedBenchmarkPool['excludedPlantIdsWithoutCoordinates'];
};

async function postBenchmarkPoolPreview (payload: IBenchmarkPoolPreviewRequest): Promise<IResolvedBenchmarkPool> {
    const response = await seriesApi.post<IBenchmarkPoolPreviewRequest, IBenchmarkPoolPreviewResponse>({
        endpoint: '/GbBenchmarkPoolPreview',
        payload,
    });
    return {
        ...response.data,
        historicalExclusions: response.data.historicalExclusions ?? [],
        excludedPlantIdsWithoutCoordinates: response.data.excludedPlantIdsWithoutCoordinates ?? [],
    };
}

export const benchmarkPoolsApi = {
    listBenchmarkPools,
    getBenchmarkPool,
    createBenchmarkPool,
    updateBenchmarkPool,
    deleteBenchmarkPool,
    postBenchmarkPoolPreview,
};
